
import React from 'react';
import type { AiRecommendation, Wine } from '../types';
// import SommelierCatIcon from './icons/SommelierCatIcon';

// 推薦オブジェクトの安全化（型ゆれ吸収）
function normalizeRecommendation(raw: AiRecommendation) {
  const idNum = typeof raw.id === "string" ? Number(raw.id) : raw.id;
  const priceNum = typeof raw.price === "string" ? Number(raw.price) : raw.price;
  return {
    id: Number.isFinite(idNum) ? idNum : -1,
    name: raw.name ?? "",
    type: raw.type ?? "",
    origin: raw.origin ?? "",
    variety: raw.variety ?? "",
    price: Number.isFinite(priceNum) ? priceNum : 0,
    reason: raw.reason ?? "",
  };
}

// 一時的にインライン定義
const SommelierCatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    aria-label="Cat Paw Icon"
    role="img"
    fill="currentColor"
  >
    <path d="M12 18.2c-1.45 2.08-4.5 2.08-5.95 0C4.6 16.12 6.55 13 9.1 13.6c.85.2 1.8.95 2.9 1.95c1.1-.99 2.05-1.75 2.9-1.95c2.55-.6 4.5 2.52 3.05 4.6c-1.45 2.08-4.5 2.08-5.95 0Z" />
    <circle cx="6.5" cy="10.5" r="2.3" />
    <circle cx="10.5" cy="8" r="2.3" />
    <circle cx="13.5" cy="8" r="2.3" />
    <circle cx="17.5" cy="10.5" r="2.3" />
  </svg>
);

interface RecommendationProps {
  recommendations: AiRecommendation[];
  wineList: Wine[];
  onReset: () => void;
}

const RecommendationCard: React.FC<{ wine: AiRecommendation, index: number, wineList: Wine[] }> = ({ wine, index, wineList }) => {
  const safe = normalizeRecommendation(wine);
  const rankColor = index === 0 ? 'bg-amber-400' : index === 1 ? 'bg-slate-300' : 'bg-amber-600';
  const rankTextColor = index === 0 ? 'text-amber-900' : index === 1 ? 'text-slate-700' : 'text-amber-100';

  // 元のワインリストから画像を取得（id型ゆれ対策で Number()）
  const originalWine = wineList.find(w => Number(w.id) === Number(safe.id));
  const wineImage = originalWine?.image;

  return (
    <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 flex flex-col sm:flex-row items-stretch">
      {/* Image Section */}
      <div className="w-full sm:w-32 flex-shrink-0 bg-gray-50/50 flex items-center justify-center p-3 sm:p-4">
        {wineImage ? (
          <img 
            src={wineImage} 
            alt={safe.name || "ワイン画像"} 
            className="max-h-32 sm:max-h-40 md:max-h-full w-auto sm:w-full object-contain rounded-lg" 
          />
        ) : (
          <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center text-gray-400">
            <SommelierCatIcon className="w-12 h-12 sm:w-16 sm:h-16 opacity-75" />
          </div>
        )}
      </div>

      {/* Details Section */}
      <div className="p-4 sm:p-6 flex-grow flex flex-col justify-center">
        <div className="mb-3 sm:mb-4">
          <span className={`inline-block px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${rankColor} ${rankTextColor} mb-2`}>
            おすすめ No.{index + 1}
          </span>
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>
            {safe.name || "名称未設定"}
          </h3>
        </div>
        
        <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-4 text-xs sm:text-sm" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
          <div className="flex items-center">
            <span className="w-16 sm:w-20 font-semibold">タイプ</span>
            <span>: {safe.type || "—"}</span>
          </div>
          <div className="flex items-center">
            <span className="w-16 sm:w-20 font-semibold">産地</span>
            <span>: {safe.origin || "—"}</span>
          </div>
          <div className="flex items-center">
            <span className="w-16 sm:w-20 font-semibold">品種</span>
            <span>: {safe.variety || "—"}</span>
          </div>
          <div className="flex items-center">
            <span className="w-16 sm:w-20 font-semibold">価格</span>
            <div className="flex-1">
              {originalWine?.isGlassWine && originalWine?.glassPrice ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 px-2 py-1 rounded-full">
                      🍷 グラス
                    </span>
                    <span className="text-sm font-bold text-blue-800">
                      ¥{Number(originalWine.glassPrice).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white bg-gradient-to-r from-gray-500 to-gray-600 px-2 py-1 rounded-full">
                      🍾 ボトル
                    </span>
                    <span className="text-sm font-bold" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>
                      ¥{Number(safe.price).toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white bg-gradient-to-r from-gray-500 to-gray-600 px-2 py-1 rounded-full">
                    🍾 ボトル
                  </span>
                  <span className="text-sm font-bold" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>
                    ¥{Number(safe.price).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-1 text-sm sm:text-base" style={{ color: 'var(--theme-text-primary, #374151)' }}>ソムリエのおすすめ理由</h4>
          <p className="leading-relaxed text-sm sm:text-base" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
            {safe.reason || "（理由の取得に失敗しました）"}
          </p>
        </div>
      </div>
    </div>
  );
};


const Recommendation: React.FC<RecommendationProps> = ({ recommendations, wineList, onReset }) => {
  return (
    <div className="w-full max-w-4xl mx-2">
      <div className="text-center mb-6 sm:mb-10">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>あなたへのおすすめワイン</h2>
        <p className="text-base sm:text-lg mt-2" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>AIソムリエが厳選したトップ3です。</p>
      </div>

      {recommendations.length > 0 ? (
        <div className="space-y-4 sm:space-y-8">
          {recommendations.map((wine, index) => {
            const id = typeof wine.id === "string" ? Number(wine.id) : wine.id;
            return <RecommendationCard key={`${id ?? 'noid'}-${index}`} wine={wine} index={index} wineList={wineList} />;
          })}
        </div>
      ) : (
        <div className="text-center bg-white/50 p-4 sm:p-8 rounded-lg">
          <p className="text-sm sm:text-base" style={{ color: 'var(--theme-text-primary, #374151)' }}>申し訳ありませんが、条件に合うワインが見つかりませんでした。</p>
        </div>
      )}

      <div className="text-center mt-8 sm:mt-12">
        <button
          onClick={onReset}
          className="w-full sm:w-auto bg-gray-700 text-white font-bold py-3 px-6 sm:px-8 rounded-lg shadow-lg hover:bg-gray-800 transition-colors duration-300 transform hover:scale-105 text-sm sm:text-base"
        >
          もう一度探す
        </button>
      </div>

      {/* フッター */}
      <div className="text-center mt-8 text-sm text-gray-500">
        <p>最終更新: {new Date().toLocaleString('ja-JP')}</p>
        <p className="mt-2">
          <span className="text-gray-400">Powered by</span> 
          <span className="ml-1 font-semibold text-red-600">Orange Concept Sommelier Lab</span>
        </p>
      </div>
    </div>
  );
};

export default Recommendation;
